// CampaignListing.jsx — BespokeBridge Communications Module
// Unified view of all campaigns across Email, Push, and WhatsApp
// Props: { orgId, adminId, onNavigateToChannel }

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getAllCampaigns,
  applyCampaignFilters,
  getRecipientDisplay,
  cancelEmailCampaign,
  cancelPushCampaign,
  cancelWhatsAppCampaign,
  duplicateEmailCampaign,
  duplicatePushCampaign,
  duplicateWhatsAppCampaign,
  seedDefaultTemplates,
  getStaffDisplayName,
  AUDIENCE_TYPES,
  fmtDate,
  fmtDateTime,
} from '../../../api/communicationsApi';

const PAGE_SIZE = 25;

function effectiveStatus(c) {
  return c.status;
}

// ── Status badge ──────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  draft:     { bg: '#F3F4F6', color: '#374151', border: '#D1D5DB' },
  scheduled: { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
  sent:      { bg: '#D1FAE5', color: '#065F46', border: '#6EE7B7' },
  failed:    { bg: '#FEF2F2', color: '#DC2626', border: '#FECACA' },
  cancelled: { bg: '#FEE2E2', color: '#991B1B', border: '#FCA5A5' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 700, border: `1px solid ${s.border}`,
      background: s.bg, color: s.color, textTransform: 'capitalize',
    }}>
      {status || 'draft'}
    </span>
  );
}

// ── Channel badge ─────────────────────────────────────────────────────────────
const CHANNEL_STYLES = {
  email:    { bg: '#EEF2FF', color: '#4338CA', border: '#C7D2FE', label: 'Email' },
  push:     { bg: '#FFF7ED', color: '#C2410C', border: '#FED7AA', label: 'Push' },
  whatsapp: { bg: '#DCFCE7', color: '#15803D', border: '#86EFAC', label: 'WhatsApp' },
};

function ChannelBadge({ channel }) {
  const s = CHANNEL_STYLES[channel] || { bg: '#F3F4F6', color: '#374151', border: '#D1D5DB', label: channel };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 700, border: `1px solid ${s.border}`,
      background: s.bg, color: s.color,
    }}>
      {s.label}
    </span>
  );
}

function AudienceLabel({ audienceType }) {
  const map = { members: 'Members', volunteers: 'Volunteers', both: 'Members + Volunteers', donors: 'Donors', all: 'Members + Volunteers + Donors' };
  return <span>{map[audienceType] || audienceType || '—'}</span>;
}

// ── View Campaign Modal ───────────────────────────────────────────────────────
function ViewModal({ campaign, onClose }) {
  if (!campaign) return null;

  const recipientDisplay = campaign._channel === 'push' && campaign.successCount != null
    ? campaign.successCount
    : getRecipientDisplay(campaign);

  return (
    <div className="bb-comm-modal-overlay">
      <div className="bb-comm-modal bb-comm-modal--wide">
        <div className="bb-comm-modal-header">
          <h4 className="bb-comm-modal-title">Campaign Details</h4>
          <button className="bb-comm-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="bb-comm-modal-body">
          <div className="bb-comm-detail-grid">
            {[
              ['Name',       campaign.name],
              ['Channel',    <ChannelBadge channel={campaign._channel} />],
              ['Status',     <StatusBadge status={campaign.status} />],
              ...(campaign._channel === 'email'
                ? [['Subject', campaign.subject || '—']]
                : campaign._channel === 'push'
                  ? [['Notification Title', campaign.title || '—']]
                  : [['Template', campaign.templateName || '—']]),
              ['Audience',   <AudienceLabel audienceType={campaign.audienceType} />],
              ['Recipients', recipientDisplay],
              ['Scheduled',  fmtDateTime(campaign.scheduledAt)],
              ['Sent',       fmtDateTime(campaign.sentAt)],
              ['Created By', campaign.createdBy || '—'],
              ['Created',    fmtDate(campaign.createdAt)],
            ].map(([lbl, val]) => (
              <div key={lbl} className="bb-comm-detail-row">
                <span className="bb-comm-detail-label">{lbl}</span>
                <span style={{ color: '#374151', fontSize: 13 }}>{val}</span>
              </div>
            ))}
          </div>

          {/* Body / message content */}
          {(campaign.body || campaign.subject) && (
            <div className="bb-comm-detail-body">
              <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                {campaign._channel === 'email' ? 'Email Body' : 'Message Body'}
              </div>
              {campaign._channel === 'email' && campaign.body && (
                <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '14px 16px', fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                  {campaign.body}
                </div>
              )}
              {campaign._channel === 'push' && campaign.body && (
                <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '14px 16px', fontSize: 13, color: '#374151' }}>
                  {campaign.body}
                </div>
              )}
              {campaign._channel === 'whatsapp' && campaign.templateVariables && (
                <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '14px 16px', fontSize: 13, color: '#374151' }}>
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 6 }}>Variables</div>
                  {Object.entries(campaign.templateVariables).map(([k, v]) => (
                    <div key={k} style={{ marginBottom: 4 }}>
                      <span style={{ fontWeight: 600 }}>{`{{${k}}}`}:</span>{' '}{v}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="bb-comm-modal-footer">
          <button className="bb-btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
export default function CampaignListing({ orgId, adminId, onNavigateToChannel }) {
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [staffNames, setStaffNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [viewCampaign, setViewCampaign] = useState(null);

  // ── Filters ───────────────────────────────────────────────────────────────
  const [filterChannel, setFilterChannel]       = useState('');
  const [filterStatus, setFilterStatus]         = useState('');
  const [filterAudience, setFilterAudience]     = useState('');
  const [filterDateFrom, setFilterDateFrom]     = useState('');
  const [filterDateTo, setFilterDateTo]         = useState('');
  const [search, setSearch]                     = useState('');
  const [page, setPage]                         = useState(1);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Data loading ──────────────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      await seedDefaultTemplates(orgId);
      const data = await getAllCampaigns(orgId);
      setAllCampaigns(data);
      // Resolve unique createdBy UIDs to display names
      const uids = [...new Set(data.map(c => c.createdBy).filter(Boolean))];
      const entries = await Promise.all(
        uids.map(async uid => [uid, await getStaffDisplayName(orgId, uid)])
      );
      setStaffNames(Object.fromEntries(entries));
    } catch (err) {
      showToast('Failed to load campaigns: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (orgId) loadAll();
  }, [orgId, loadAll]);

  // ── Filtered list (memoised) ──────────────────────────────────────────────
  const filtered = useMemo(() => {
    return applyCampaignFilters(allCampaigns, {
      channel:     filterChannel  || undefined,
      status:      filterStatus   || undefined,
      audienceType: filterAudience || undefined,
      search:      search         || undefined,
      dateFrom:    filterDateFrom || undefined,
      dateTo:      filterDateTo   || undefined,
    });
  }, [allCampaigns, filterChannel, filterStatus, filterAudience, search, filterDateFrom, filterDateTo]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setFilterChannel('');
    setFilterStatus('');
    setFilterAudience('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setSearch('');
    setPage(1);
  };

  const hasFilters = filterChannel || filterStatus || filterAudience || filterDateFrom || filterDateTo || search;

  // ── Cancel ────────────────────────────────────────────────────────────────
  const handleCancel = async (c) => {
    if (!window.confirm(`Cancel campaign "${c.name}"?`)) return;
    try {
      if (c._channel === 'email')    await cancelEmailCampaign(orgId, c.id);
      if (c._channel === 'push')     await cancelPushCampaign(orgId, c.id);
      if (c._channel === 'whatsapp') await cancelWhatsAppCampaign(orgId, c.id);
      showToast('Campaign cancelled.');
      loadAll();
    } catch (err) {
      showToast('Cancel failed: ' + err.message, 'error');
    }
  };

  // ── Duplicate ─────────────────────────────────────────────────────────────
  const handleDuplicate = async (c) => {
    try {
      if (c._channel === 'email')    await duplicateEmailCampaign(orgId, c.id, adminId);
      if (c._channel === 'push')     await duplicatePushCampaign(orgId, c.id, adminId);
      if (c._channel === 'whatsapp') await duplicateWhatsAppCampaign(orgId, c.id, adminId);
      showToast('Campaign duplicated.');
      loadAll();
    } catch (err) {
      showToast('Duplicate failed: ' + err.message, 'error');
    }
  };

  // ── Edit — navigate to channel ────────────────────────────────────────────
  const handleEdit = (c) => {
    if (onNavigateToChannel) {
      onNavigateToChannel(c._channel, c.id);
    } else {
      showToast('Navigation not configured.', 'error');
    }
  };

  // ── Summary counters ──────────────────────────────────────────────────────
  const counts = useMemo(() => {
    const c = { email: 0, push: 0, whatsapp: 0 };
    allCampaigns.forEach(cam => { if (c[cam._channel] !== undefined) c[cam._channel]++; });
    return c;
  }, [allCampaigns]);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="bb-comm-root">

      {/* Header + summary chips */}
      <div className="bb-comm-section-header" style={{ marginBottom: 16 }}>
        <h3 className="bb-comm-section-title">Campaign Listing</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { key: 'email',    label: 'Email',    style: CHANNEL_STYLES.email },
            { key: 'push',     label: 'Push',     style: CHANNEL_STYLES.push },
            { key: 'whatsapp', label: 'WhatsApp', style: CHANNEL_STYLES.whatsapp },
          ].map(({ key, label, style }) => (
            <span key={key} style={{
              padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              background: style.bg, color: style.color, border: `1px solid ${style.border}`,
            }}>
              {label}: {counts[key]}
            </span>
          ))}
        </div>
      </div>

      {/* Filter row */}
      <div className="bb-comm-filters-row" style={{ flexWrap: 'wrap', rowGap: 8 }}>
        <input
          className="bb-form-input bb-comm-search"
          placeholder="Search by campaign name…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <select
          className="bb-form-select bb-comm-filter-select"
          value={filterChannel}
          onChange={e => { setFilterChannel(e.target.value); setPage(1); }}
        >
          <option value="">All Channels</option>
          <option value="email">Email</option>
          <option value="push">Push</option>
          <option value="whatsapp">WhatsApp</option>
        </select>
        <select
          className="bb-form-select bb-comm-filter-select"
          value={filterStatus}
          onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select
          className="bb-form-select bb-comm-filter-select"
          value={filterAudience}
          onChange={e => { setFilterAudience(e.target.value); setPage(1); }}
        >
          <option value="">All Audiences</option>
          {AUDIENCE_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>From</span>
          <input
            type="date"
            className="bb-form-input"
            style={{ width: 150 }}
            value={filterDateFrom}
            onChange={e => { setFilterDateFrom(e.target.value); setPage(1); }}
          />
          <span style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>To</span>
          <input
            type="date"
            className="bb-form-input"
            style={{ width: 150 }}
            value={filterDateTo}
            onChange={e => { setFilterDateTo(e.target.value); setPage(1); }}
          />
        </div>
        {hasFilters && (
          <button className="bb-comm-link-btn" onClick={resetFilters}>Clear filters</button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="bb-comm-loading">Loading all campaigns…</div>
      ) : (
        <>
          {/* Result count */}
          <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 10 }}>
            {filtered.length} campaign{filtered.length !== 1 ? 's' : ''} found
            {hasFilters ? ` (filtered from ${allCampaigns.length} total)` : ''}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="bb-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Campaign Name</th>
                  <th>Channel</th>
                  <th>Audience</th>
                  <th>Status</th>
                  <th>Recipients</th>
                  <th>Sent / Scheduled</th>
                  <th>Created By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="bb-comm-empty-row">
                      {allCampaigns.length === 0
                        ? 'No campaigns yet. Head to Email, Push, or WhatsApp to create your first campaign.'
                        : 'No campaigns match your filters. Try clearing some filters.'}
                    </td>
                  </tr>
                ) : paged.map(c => {
                  const recipientDisplay = c._channel === 'push' && c.successCount != null
                    ? c.successCount
                    : getRecipientDisplay(c);
                  const displayStatus = effectiveStatus(c);
                  return (
                    <tr key={`${c._channel}-${c.id}`}>
                      <td className="bb-comm-cell--name" style={{ maxWidth: 200 }}>
                        {c.name}
                        {c._channel === 'email' && c.subject && (
                          <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 400, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.subject}
                          </div>
                        )}
                        {c._channel === 'push' && c.title && (
                          <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 400, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.title}
                          </div>
                        )}
                      </td>
                      <td><ChannelBadge channel={c._channel} /></td>
                      <td><AudienceLabel audienceType={c.audienceType} /></td>
                      <td><StatusBadge status={displayStatus} /></td>
                      <td style={{ fontSize: 13 }}>
                        {c._channel === 'whatsapp' && c.totalAudienceCount > 0 ? (
                          <span>
                            {c.eligibleRecipientCount || 0}
                            <span style={{ color: '#9CA3AF', fontSize: 11 }}> / {c.totalAudienceCount} opted-in</span>
                          </span>
                        ) : (
                          recipientDisplay
                        )}
                      </td>
                      <td className="bb-comm-cell--muted">
                        {c.sentAt
                          ? fmtDate(c.sentAt)
                          : c.scheduledAt
                            ? fmtDateTime(c.scheduledAt)
                            : '—'}
                      </td>
                      <td className="bb-comm-cell--muted">{(c.createdBy && staffNames[c.createdBy]) || c.createdBy || '—'}</td>
                      <td className="bb-comm-cell--actions">
                        <button className="bb-comm-action-btn" onClick={() => setViewCampaign(c)}>View</button>
                        {' '}
                        {(displayStatus === 'draft' || displayStatus === 'scheduled') && (
                          <><button className="bb-comm-action-btn" onClick={() => handleEdit(c)}>Edit</button>{' '}</>
                        )}
                        <button className="bb-comm-action-btn" onClick={() => handleDuplicate(c)}>Duplicate</button>
                        {' '}
                        {(displayStatus === 'draft' || displayStatus === 'scheduled') && (
                          <button className="bb-comm-action-btn bb-comm-action-btn--danger" onClick={() => handleCancel(c)}>Cancel</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 16, fontSize: 13 }}>
              <button className="bb-btn-secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</button>
              <span style={{ color: '#6B7280' }}>Page {page} of {totalPages}</span>
              <button className="bb-btn-secondary" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</button>
            </div>
          )}
        </>
      )}

      {/* View modal */}
      {viewCampaign && <ViewModal campaign={viewCampaign} onClose={() => setViewCampaign(null)} />}

      {/* Toast */}
      {toast && (
        <div className={`bb-comm-toast bb-comm-toast--${toast.type}`} onClick={() => setToast(null)}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
